import { useState } from "react";
import { KeyRound, Copy, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const randomPwd = () => {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  return Array.from(crypto.getRandomValues(new Uint32Array(10)), (n) => chars[n % chars.length]).join("");
};

export const ResetPasswordButton = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const openDialog = () => { setPwd(randomPwd()); setDone(false); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/password`, { password: pwd });
      setDone(true);
      toast.success(`Password di ${user.nome} reimpostata`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(`${user.email} / ${pwd}`); toast.success("Credenziali copiate"); };

  return (
    <>
      <button data-testid={`user-reset-password-btn-${user.id}`} onClick={openDialog} title="Reimposta password"
        className="w-9 h-9 grid place-items-center border border-zinc-200 hover:bg-zinc-950 hover:text-white transition-colors duration-150">
        <KeyRound className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Reimposta password</DialogTitle>
            <DialogDescription>{user.nome} {user.cognome} · {user.email}</DialogDescription>
          </DialogHeader>
          <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-600">Nuova password (min. 6)</label>
          <div className="flex gap-2">
            <input data-testid="reset-password-input" value={pwd} onChange={(e) => { setPwd(e.target.value); setDone(false); }}
              className="flex-1 h-11 px-3 border border-zinc-300 font-mono text-sm focus:outline-none focus:border-zinc-950" />
            <button data-testid="reset-password-generate-btn" onClick={() => { setPwd(randomPwd()); setDone(false); }} title="Genera"
              className="w-11 h-11 grid place-items-center border border-zinc-300 hover:bg-zinc-950 hover:text-white transition-colors duration-150">
              <Shuffle className="w-4 h-4" />
            </button>
          </div>
          {done ? (
            <div className="border border-[#059669] bg-[#ECFDF5] p-3 flex items-center justify-between gap-3" data-testid="reset-password-success">
              <p className="text-sm">Password aggiornata. Comunicala allo studente.</p>
              <button data-testid="reset-password-copy-btn" onClick={copy} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[#0D9488]"><Copy className="w-4 h-4" /> Copia</button>
            </div>
          ) : (
            <button data-testid="reset-password-save-btn" onClick={save} disabled={busy || pwd.length < 6}
              className="h-11 bg-[#0D9488] text-white text-sm font-semibold hover:bg-[#059669] disabled:opacity-40 transition-colors duration-150">
              {busy ? "Salvataggio…" : "Salva nuova password"}
            </button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
